import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrashAlt,
  faEye,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Assignmentpage/Assignmentpage.css";
import AddAssignment from "../AddAssignment/AddAssignment";
import ViewAssignment from "../ViewAssignment/ViewAssignment";
import { useNavigate } from "react-router-dom";

const API_URL = "https://aadyaacademy.org/api";

const Assignmentpage = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddAssignmentOpen, setIsAddAssignmentOpen] = useState(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState(null);
  const [viewAssignment, setViewAssignment] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const teacherPhone = localStorage.getItem("userPhone");
        if (!teacherPhone) {
          navigate("/login");
          return;
        }
        const response = await axios.get(`${API_URL}/teacher-assignments/${teacherPhone}`);
        console.log("Assignments fetched:", response.data);

        // Sort assignments by date in descending order (newest first)
        const sortedData = response.data.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // Descending order
        });
        console.log("Sorted assignments:", sortedData);

        setTableData(sortedData);
      } catch (error) {
        console.error("Error fetching assignments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [navigate]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleAddAssignment = () => {
    console.log("Opening AddAssignment form");
    setAssignmentToEdit(null);
    setIsAddAssignmentOpen(true);
  };

  const handleCloseAddAssignment = () => {
    console.log("Closing AddAssignment form");
    setIsAddAssignmentOpen(false);
    setAssignmentToEdit(null);
  };

  const handleEdit = (assignment) => {
    console.log("Editing assignment:", assignment);
    setAssignmentToEdit(assignment);
    setIsAddAssignmentOpen(true);
  };

  const handleView = (assignment) => {
    setViewAssignment(assignment);
  };

  const handleDelete = async (assignmentId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this assignment?");
    console.log(`Delete confirmation for assignment ${assignmentId}: ${confirmDelete ? "Confirmed" : "Canceled"}`);

    if (!confirmDelete) {
      return; // Exit if user clicks "Cancel"
    }

    try {
      const response = await axios.delete(`${API_URL}/assignments/${assignmentId}`);
      console.log("Assignment deleted successfully", response.data);
      setTableData(tableData.filter((item) => item.id !== assignmentId));
      toast.success("Assignment deleted successfully!"); // Add success toast
    } catch (error) {
      console.error("Error deleting assignment:", error.response || error.message);
      toast.error("Failed to delete assignment"); // Replace alert with toast
    }
  };

  const handleCloseView = () => {
    setViewAssignment(null);
  };

  const handleAssignmentAdded = (newAssignment) => {
    if (assignmentToEdit) {
      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === newAssignment.id ? newAssignment : item
        )
      );
    } else {
      setTableData((prevData) => {
        const updatedData = [...prevData, newAssignment];
        // Re-sort after adding a new assignment
        return updatedData.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA;
        });
      });
    }
    setIsAddAssignmentOpen(false);
    setAssignmentToEdit(null);
  };

  const filteredData = tableData.filter((item) => {
    const searchFields = [
      item.class || "",
      item.section || "",
      item.subject || "",
      item.date || "",
      item.day || "",
      item.text_message || "",
    ];

    return searchFields.some((field) =>
      field.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (isAddAssignmentOpen) {
    return (
      <div className="add-assignment-form">
        <AddAssignment
          closeForm={handleCloseAddAssignment}
          assignment={assignmentToEdit}
          onAssignmentAdded={handleAssignmentAdded}
        />
      </div>
    );
  }

  if (viewAssignment) {
    return (
      <div className="view-assignment-form">
        <ViewAssignment
          assignment={viewAssignment}
          closeDetail={handleCloseView}
        />
      </div>
    );
  }

  return (
    <div className="assignmentPage">
      <ToastContainer />
      <div className="assignmentTable">
        <div
          className="table-header"
          style={{
            minWidth: "400px",
            maxWidth: "none",
            padding: "15px",
            boxSizing: "border-box",
            overflow: "auto",
          }}
        >
          <h3 style={{ margin: "0", fontSize: "20px" }}>Assignments</h3>
          <div className="table-header-btm">
            <button
              onClick={handleAddAssignment}
              className="add-button-assignment"
              style={{
                padding: "8px 15px",
                fontSize: "14px",
              }}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: "5px" }} />
              Add New
            </button>
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th className="thead-content">Grade</th>
                <th className="thead-content">Section</th>
                <th className="thead-content">Subject</th>
                <th className="thead-content">Date</th>
                <th className="thead-content">Day</th>
                <th className="thead-content">Attachment/Message</th>
                <th className="thead-content">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No assignments found
                  </td>
                </tr>
              ) : (
                filteredData.map((assignment) => (
                  <tr key={assignment.id}>
                    <td className="tbody-content">{assignment.class || "N/A"}</td>
                    <td className="tbody-content">{assignment.section || "N/A"}</td>
                    <td className="tbody-content">{assignment.subject || "N/A"}</td>
                    <td className="tbody-content">{formatDate(assignment.date)}</td>
                    <td className="tbody-content">{assignment.day || "N/A"}</td>
                    <td className="tbody-content">
                      {assignment.text_message ? (
                        <span>{assignment.text_message}</span>
                      ) : assignment.attachmentUrl ? (
                        <a
                          href={assignment.attachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Attachment
                        </a>
                      ) : (
                        "No attachment/message"
                      )}
                    </td>
                    <td className="actions">
                      <button
                        className="view-btn"
                        onClick={() => handleView(assignment)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(assignment)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(assignment.id)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Assignmentpage;